import React, { memo } from 'react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { ENROLLMENT_FORM_STEPS, EnrollmentStatus, UserStatus } from '../../../core/constant/constant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store';

interface FormNavigationProps {
  activeStep: number;
  handleBack: () => void;
  handleNext: () => void;
  isSaving?: boolean;
  isAdditionalInfo?: boolean;
  // isBlocked?: boolean;
  // blockReason?: string;
}

const FormNavigation: React.FC<FormNavigationProps> = ({
  activeStep,
  handleBack,
  handleNext,
  isSaving = false,
  isAdditionalInfo = false,
  // isBlocked = false,
  // blockReason,
}) => {
  const isFinalStep = activeStep === ENROLLMENT_FORM_STEPS.length - 1;
  const theme = useTheme();
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription);
  const isMemberSubscriptionActive =
    memberSubscription?.status === EnrollmentStatus.MEMBERSHIP_ACTIVE &&
    memberSubscription.firstPaymentId;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 4,
        pt: 2,
        pb: { xs: 2, sm: 0 },
      }}
    >
      {/* {isBlocked && blockReason && (
        <Typography 
          color="error" 
          sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 'medium' }}
        >
          {blockReason}
        </Typography>
      )} */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {activeStep > 0 && (
          <Button
            variant='outlined'
            onClick={handleBack}
            disabled={activeStep === 0 || isSaving}
            sx={{
              minWidth: 100,
              order: { xs: 1, sm: 1 },
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            Back
          </Button>
        )}

        <Box
          sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' }, minWidth: { sm: '100px' } }}
        />
        {isAdditionalInfo ? (
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={isSaving}
            color={isFinalStep ? 'secondary' : 'primary'}
            sx={{
              minWidth: isFinalStep ? 140 : 130,
              position: 'relative',
              gap: 1,
              fontWeight: isFinalStep ? 'bold' : 'normal',
              order: { xs: 2, sm: 2 },
              flexGrow: { xs: 1, sm: 0 },
              background: theme.palette.primary[400],
              '&:hover': {
                background: theme.palette.primary[500],
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                <Typography variant='button' component='span'>
                  {isFinalStep ? 'Completing...' : 'Saving...'}
                </Typography>
              </>
            ) : isFinalStep ? (
              'Submit'
            ) : (
              'Next'
            )}
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={isSaving}
            // color={isFinalStep ? 'secondary' : 'primary'}
            sx={{
              minWidth: isFinalStep ? 140 : 130,
              position: 'relative',
              gap: 1,
              fontWeight: isFinalStep ? 'bold' : 'normal',
              order: { xs: 2, sm: 2 },
              flexGrow: { xs: 1, sm: 0 },
              background: theme.palette.primary[400],
              '&:hover': {
                background: theme.palette.primary[500],
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                <Typography variant='button' component='span'>
                  {isFinalStep ? 'Completing...' : 'Saving...'}
                </Typography>
              </>
            ) : (
              <>
                {isFinalStep && <CheckCircleIcon sx={{ mr: 0.5 }} />}
                {isFinalStep ? (
                  // COMMUNITY_MEMBER or SIGNED_UP with incomplete enrollment: show "Select Plan"
                  // With completed enrollment: show "Continue" (they already paid)
                  ((basicInfo.currentStatus === UserStatus.COMMUNITY_MEMBER ||
                    basicInfo.currentStatus === UserStatus.SIGNED_UP) &&
                    !basicInfo.userEnrollment?.completed)
                    ? 'Select Plan'
                    : (isMemberSubscriptionActive ? 'Continue' : 'Select Plan')
                ) : 'Next'}
              </>
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default memo(FormNavigation);
